import * as React from 'react';
import { Link } from 'react-router';
import {
  ShieldCheckIcon,
  ChevronDownIcon,
  MenuIcon,
  MapIcon,
  SmartphoneIcon,
  ClipboardListIcon,
  ArrowRightIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { UserMenu } from '@/components/auth/user-menu';
import { appIdentity } from '@/app/app-config';
import { PURPOSES } from '@/lib/purposes';

/* ─── Desktop "Who it's for" dropdown ───────────────────────────── */

function PurposeMenu() {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm">
          Who it&rsquo;s for
          <ChevronDownIcon className="ml-1 h-3.5 w-3.5" aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-muted-foreground">
          Who it&rsquo;s for
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {PURPOSES.map((purpose) => (
          <DropdownMenuItem key={purpose.slug} asChild>
            <Link to={`/for/${purpose.slug}`} className="flex items-start gap-2.5">
              <purpose.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">{purpose.shortName}</span>
                <span className="text-xs text-muted-foreground">{purpose.tagline}</span>
              </div>
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/* ─── Mobile navigation sheet ───────────────────────────────────── */

function MobileNav() {
  const [open, setOpen] = React.useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" aria-label="Open navigation menu">
          <MenuIcon className="h-5 w-5" aria-hidden />
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-72">
        <SheetHeader>
          <SheetTitle>Navigation</SheetTitle>
        </SheetHeader>
        <nav className="mt-4 flex flex-col gap-1" aria-label="Mobile navigation">
          <span className="px-2 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Who it&rsquo;s for
          </span>
          {PURPOSES.map((purpose) => (
            <Link
              key={purpose.slug}
              to={`/for/${purpose.slug}`}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm hover:bg-accent"
            >
              <purpose.icon className="h-4 w-4 text-primary" aria-hidden />
              <span className="font-medium">{purpose.shortName}</span>
            </Link>
          ))}
          <span className="px-2 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Explore
          </span>
          {[
            { to: '/map', label: 'Map', icon: MapIcon },
            { to: '/ar', label: 'AR View', icon: SmartphoneIcon },
            { to: '/track', label: 'Review', icon: ClipboardListIcon },
          ].map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className="flex items-center gap-2.5 rounded-md px-2 py-2 text-sm hover:bg-accent"
            >
              <item.icon className="h-4 w-4 text-muted-foreground" aria-hidden />
              {item.label}
            </Link>
          ))}
          <Link
            to="/report?purpose=councils"
            onClick={() => setOpen(false)}
            className="mt-3 flex items-center justify-center gap-2 rounded-md bg-primary px-3 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Report an Issue
            <ArrowRightIcon className="h-4 w-4" aria-hidden />
          </Link>
        </nav>
      </SheetContent>
    </Sheet>
  );
}

/* ─── Shared public header ──────────────────────────────────────── */

export function PublicHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
          <ShieldCheckIcon className="h-5 w-5 text-primary" aria-hidden />
          {appIdentity.name}
        </Link>
        <nav className="hidden items-center gap-1 sm:flex" aria-label="Main navigation">
          <PurposeMenu />
          <Button asChild variant="ghost" size="sm">
            <Link to="/map">Map</Link>
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/ar">AR View</Link>
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/track">Review</Link>
          </Button>
          <UserMenu />
        </nav>
        <div className="flex items-center gap-1 sm:hidden">
          <UserMenu />
          <MobileNav />
        </div>
      </div>
    </header>
  );
}
